import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { ChevronLeft, ChevronRight, X, Gift, Flame } from 'lucide-react';
import { Mandala } from './Mandala';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const monthShift = (month, delta) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (month) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

/** The month laid out as day-mandalas: every day you showed up, and every thirty-day marker. */
const MonthGrid = ({ month, days, milestones, today }) => {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const total = new Date(y, m, 0).getDate();
  const byDate = Object.fromEntries(days.map((d) => [d.date, d]));
  const cells = [
    ...Array(first.getDay()).fill(null),
    ...Array.from({ length: total }, (_, i) =>
      `${month}-${String(i + 1).padStart(2, '0')}`),
  ];

  return (
    <div className="space-y-2" data-testid="mandala-month-grid">
      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAYS.map((day) => (
          <span key={day} className="text-center text-[10px] uppercase tracking-widest
                                     text-white/30">{day}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((iso, index) => {
          if (!iso) return <span key={`pad-${index}`} />;
          const day = byDate[iso];
          const active = (day?.percent || 0) > 0;
          const isToday = iso === today;
          const milestone = milestones.includes(iso);
          return (
            <div key={iso}
                 className={`relative flex flex-col items-center justify-center rounded-xl
                             py-2 transition-colors duration-200
                   ${active ? 'bg-amber-300/[0.08]' : 'bg-white/[0.02]'}
                   ${isToday ? 'ring-1 ring-amber-300/70' : ''}`}
                 title={active ? `${day.percent}% on ${iso}` : iso}
                 data-testid={`calendar-day-${iso}`}>
              {active ? (
                <Mandala windows={day.windows} size={30} petals={false} weekPercent={day.percent} />
              ) : (
                <span className="block w-[30px] h-[30px] rounded-full border
                                 border-white/[0.08]" />
              )}
              <span className={`mt-1 text-[10px] ${active ? 'text-amber-100/80' : 'text-white/30'}`}>
                {Number(iso.slice(-2))}
              </span>
              {milestone && (
                <Gift className="absolute -top-1 -right-1 w-3.5 h-3.5 text-amber-300"
                      data-testid={`calendar-milestone-${iso}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Stat = ({ label, value, note, icon: Icon, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={testid}>
    <p className="text-xs text-white/45">{label}</p>
    <p className="mt-2 flex items-center gap-2 text-2xl font-semibold text-white">
      {Icon && <Icon className="w-5 h-5 text-amber-300/80" />}{value}
    </p>
    {note && <p className="text-[11px] text-white/35 mt-1">{note}</p>}
  </div>
);

/** The whole practice at a glance — reached by the small mandala button on the builder. */
export const MandalaCalendar = ({ onClose }) => {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback((target) => {
    axios.get(`${API}/companion/rituals/history`, { params: { month: target } })
      .then((r) => { setData(r.data); setMonth(r.data.month); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(undefined); }, [load]);

  const progress = data
    ? Math.round(((30 - data.days_to_next_milestone) / 30) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-[115] overflow-y-auto bg-[#070a16]/96 backdrop-blur-sm"
         data-testid="mandala-calendar">
      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Your consistency</h2>
            <p className="text-sm text-white/50 mt-1">
              Every day you showed up, however briefly. A missed day never undoes a month.
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                  data-testid="mandala-calendar-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!data ? (
          <p className="text-sm text-white/50 py-16 text-center">Gathering your days…</p>
        ) : (
          <>
            <div className="rounded-2xl border border-amber-300/25 bg-amber-300/[0.05] p-6
                            space-y-3" data-testid="mandala-next-milestone">
              <p className="text-sm text-white/80">
                {data.days_to_next_milestone === 0
                  ? 'Your next marker lands today.'
                  : <>Your next marker after <span className="text-amber-200 font-semibold">
                      {data.days_to_next_milestone} more day
                      {data.days_to_next_milestone === 1 ? '' : 's'}</span> of practice</>}
              </p>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-amber-400 transition-all duration-700"
                     style={{ width: `${Math.max(3, progress)}%` }} />
              </div>
              <p className="text-[11px] text-white/40">
                {data.active_days} of {data.next_milestone_at} days · marker at every thirty
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Current run" value={`${data.current_streak} days`} icon={Flame}
                    note="Yesterday still counts" testid="stat-current-streak" />
              <Stat label="Longest run" value={`${data.longest_streak} days`}
                    testid="stat-longest-streak" />
              <Stat label="Markers earned" value={data.milestones_earned}
                    note="They live in My milestones" testid="stat-milestones" />
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => load(monthShift(month, -1))}
                        className="rounded-full border border-white/12 p-1.5 text-white/50
                                   hover:text-white transition-colors"
                        data-testid="calendar-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <p className="text-sm text-white" data-testid="calendar-month-label">
                  {monthLabel(month)}
                </p>
                <button onClick={() => load(monthShift(month, 1))}
                        className="rounded-full border border-white/12 p-1.5 text-white/50
                                   hover:text-white transition-colors"
                        data-testid="calendar-next-month">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <MonthGrid month={month} days={data.days} today={data.today}
                         milestones={data.milestone_days || []} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MandalaCalendar;
